import { useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { PodgladTelefonu } from './PodgladTelefonu';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PODGLĄD 1:1 — PRAWDZIWA APLIKACJA, NIE MAKIETA (OWNER 17.10)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Wnętrze telefonu to PRAWDZIWY build aplikacji mobilnej (podgląd R5-10 na kierowcy.tuszama.pl: te same komponenty,
 * nagłówek, logo, pasek, sekcje i nawigacja co w sklepie), któremu podgląd podaje paczkę skórki jako odpowiedź
 * `/api/app/seasonal-theme` — dokładnie tak, jak zrobi to serwer. Paczki w podglądzie są bajt w bajt tymi samymi
 * plikami co biblioteka CC (sprawdzone 17.10: 23/23 identyczne).
 *
 * Skórka spoza podglądu R5-10 (np. wgrana później w CC) nie ma tam swojego katalogu — wtedy pokazujemy makietę CC
 * z JAWNYM opisem „przybliżenie”, a nie udajemy 1:1.
 */
export const ADRES_PODGLADU_APLIKACJI = 'https://kierowcy.tuszama.pl/nowa-apka-mobile.html';

/** Katalog skórki w podglądzie aplikacji = `themeId` paczki źródłowej (małe litery, cyfry, myślnik). */
export function katalogWPodgladzie(paczka: unknown): string | null {
  const o = (paczka && typeof paczka === 'object' ? paczka : null) as { themeId?: unknown; object?: { themeId?: unknown } } | null;
  const id = typeof o?.themeId === 'string' ? o.themeId : typeof o?.object?.themeId === 'string' ? o.object.themeId : null;
  return id && /^[a-z0-9][a-z0-9-]{0,62}$/.test(id) ? id : null;
}

export function adresPodgladuAplikacji(katalog: string): string {
  const u = new URL(ADRES_PODGLADU_APLIKACJI);
  u.searchParams.set('skorka', `katalog:${katalog}`);
  u.searchParams.set('ekran', 'menu');
  return u.toString();
}

/**
 * WYCINEK = SAM TELEFON. Strona R5-10 to symulator: nad telefonem ma nagłówek QA (gałąź, rozmiary, wybór skórki).
 * Przy szerokości 1000 px układ jest stały — nagłówek w jednej kolumnie wierszy, telefon w rozmiarze 384×832 w ramce
 * 404×852, której lewy górny róg leży w (298; 212,7) (zmierzone 17.10 w Chrome). Ramka w CC pokazuje wyłącznie ten
 * prostokąt: zostaje ramka telefonu i prawdziwa aplikacja w środku, nagłówek QA jest poza kadrem.
 * Zmiana układu strony R5-10 = zmiana tych liczb (zależność R5-10: tryb osadzenia bez nagłówka).
 */
export const KADR_PODGLADU = { strona: { szer: 1000, wys: 1080 }, telefon: { x: 298, y: 212.6875, szer: 404, wys: 852 } } as const;

export function PodgladAplikacji({ paczka, nazwa }: { paczka: unknown; nazwa: string }) {
  const katalog = katalogWPodgladzie(paczka);
  const [makieta, setMakieta] = useState(false);

  if (!katalog || makieta) {
    return (
      <Stack spacing={1}>
        <Alert severity="warning" role="note" data-testid="podglad-przyblizenie">
          {katalog
            ? 'Makieta Control Center — przybliżenie układu. Wierny wygląd: „Prawdziwa aplikacja”.'
            : 'Tej skórki nie ma w podglądzie prawdziwej aplikacji (R5-10) — poniżej makieta Control Center, czyli PRZYBLIŻENIE układu, nie ekran 1:1.'}
        </Alert>
        {katalog ? <Box><Button size="small" onClick={() => setMakieta(false)}>Prawdziwa aplikacja</Button></Box> : null}
        <PodgladTelefonu paczka={paczka} />
      </Stack>
    );
  }

  return (
    <Stack spacing={1} sx={{ alignItems: 'center' }} data-testid="podglad-aplikacji">
      <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
        Prawdziwa aplikacja (podgląd R5-10, ekran Menu, telefon 384×832) z paczką tej skórki — ten sam plik co w bibliotece.
        Dane menu w podglądzie są przykładowe.
      </Typography>
      <Box sx={{ position: 'relative', overflow: 'hidden', width: KADR_PODGLADU.telefon.szer, height: KADR_PODGLADU.telefon.wys,
        flex: 'none', borderRadius: '48px', bgcolor: '#111' }} data-testid="kadr-telefonu">
        <Box component="iframe" title={`Prawdziwa aplikacja ze skórką: ${nazwa}`} src={adresPodgladuAplikacji(katalog)}
          sx={{ position: 'absolute', left: -KADR_PODGLADU.telefon.x, top: -KADR_PODGLADU.telefon.y,
            width: KADR_PODGLADU.strona.szer, height: KADR_PODGLADU.strona.wys, border: 0 }}
          loading="lazy" referrerPolicy="no-referrer" />
      </Box>
      <Stack direction="row" spacing={1}>
        <Button size="small" component="a" href={adresPodgladuAplikacji(katalog)} target="_blank" rel="noopener noreferrer">Otwórz w nowej karcie</Button>
        <Button size="small" onClick={() => setMakieta(true)}>Makieta CC</Button>
      </Stack>
    </Stack>
  );
}
